import { Chip, ClickAwayListener, Popper } from "@mui/material";
import React from "react";

const SHOW_COUNT = 9;

const FILTER_ORDER = [
  "Shape", "Size", "Carat", "Color", "Clarity", "Cut", "Polish", "Sym",
  "Fluor", "Lab", "Location",
  "Length", "Width", "Height", "Ratio", "Table%", "Depth%", "Crown Angle", "Pav Angle",
];

const sortEntries = (entries) => {
  const ordered = [];
  const remaining = [...entries];

  FILTER_ORDER.forEach((label) => {
    const idx = remaining.findIndex(([k]) => k === label);
    if (idx !== -1) ordered.push(remaining.splice(idx, 1)[0]);
  });

  return [...ordered, ...remaining];
};

// For range values like "0.30 - 1.00" show as-is.
// For discrete comma-separated values show first 2 + "+N" if more.
const formatValue = (value) => {
  const str = String(value).toUpperCase();
  // Range: contains " - " between two numeric-like tokens
  if (/^[\d.]+ - [\d.]+$/.test(str.trim())) return str;

  const parts = str.split(", ");
  if (parts.length <= 2) return str;
  return `${parts[0]}, ${parts[1]} +${parts.length - 2}`;
};

const chipSx = {
  maxWidth: 140,
  height: 20,
  "& .MuiChip-label": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
    fontSize: "0.54rem",
    fontStyle: "italic",
    color: "#8D8D8D",
    padding: "0 6px",
  },
  "& .MuiChip-deleteIcon": {
    fontSize: "0.8rem",
    margin: "0 4px 0 -2px",
    color: "#B0B0B0",
  },
  "& .MuiChip-deleteIcon:hover": {
    color: "#1E90FF",
  },
};

const AppliedFilterChips = ({ data, onRemove }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const sortedEntries = sortEntries(Object.entries(data));
  const visible = sortedEntries.slice(0, SHOW_COUNT);
  const overflow = sortedEntries.slice(SHOW_COUNT);

  const handleMouseEnter = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => setAnchorEl(null);
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Popper open={open} anchorEl={anchorEl} style={{ zIndex: 1000 }}>
        <ClickAwayListener onClickAway={handleClose}>
          <div className="border border-gray-300 flex flex-col gap-1.5 px-3 py-3 rounded-lg bg-white mt-2 max-w-[480px] shadow-md">
            {overflow.map(([key, value]) => (
              <div key={key} className="flex items-start justify-between gap-2 leading-tight">
                <div className="flex flex-col">
                  <span className="text-[0.5rem] font-semibold text-[#8D8D8D] uppercase tracking-wide">
                    {key}
                  </span>
                  <span className="text-[0.56rem] text-[#333] break-words whitespace-normal">
                    {formatValue(value)}
                  </span>
                </div>
                {onRemove && (
                  <button
                    type="button"
                    title={`Clear ${key}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(key);
                    }}
                    className="text-[#B0B0B0] hover:text-[#1E90FF] text-[0.6rem] leading-none shrink-0 mt-0.5"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </ClickAwayListener>
      </Popper>

      <div className="flex gap-1 items-center">
        {visible.map(([key, value]) => (
          <Chip
            key={key}
            label={`${key}: ${formatValue(value)}`}
            title={`${key}: ${formatValue(value)}`}
            sx={chipSx}
            onDelete={
              onRemove
                ? (e) => {
                    e.stopPropagation();
                    onRemove(key);
                  }
                : undefined
            }
          />
        ))}

        {overflow.length > 0 && (
          <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <Chip
              label={`+${overflow.length} more`}
              sx={{
                ...chipSx,
                maxWidth: 100,
                cursor: "pointer",
                "& .MuiChip-label": {
                  ...chipSx["& .MuiChip-label"],
                  color: "#1E90FF",
                },
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default AppliedFilterChips;